import { useEffect, useState } from "react";
import { api, fileToBase64 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Camera, Loader2, Check, RefreshCw, ThumbsUp, ThumbsDown, Plus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const bust_ = (url, t) => `${url}${url.includes("?") ? "&" : "?"}t=${t}`;

const readFiles = async (files) => {
  const out = [];
  for (const f of Array.from(files || [])) out.push(await fileToBase64(f));
  return out;
};

/**
 * Your Twin: photos of you -> preview render -> "Love it?" loop. Used in onboarding and Settings.
 * Colours as meaning: good (green) = approved twin, wait (amber) = needs your verdict, action (lime) = upload.
 */
export const TwinSetup = ({ profile: initial, onChange, compact = false }) => {
  const [profile, setProfile] = useState(initial || null);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState("idle"); // idle | rendering | ready | error
  const [bust, setBust] = useState(Date.now());

  useEffect(() => { if (!initial) api.getProfile().then((p) => { setProfile(p); setNotes(p.notes || ""); }).catch(() => setProfile({})); }, [initial]);
  useEffect(() => { if (initial) { setProfile(initial); setNotes(initial.notes || ""); } }, [initial]);
  const update = async (payload, label) => {
    const p = await api.updateProfile(payload);
    setProfile(p); setBust(Date.now()); onChange?.(p);
    if (label) toast.success(label);
    return p;
  };

  const upload = (kind) => async (e) => {
    const files = e.target.files; e.target.value = "";
    if (!files?.length) return;
    setBusy(kind);
    try {
      const b64s = await readFiles(files);
      if (kind === "refs") await update({ add_reference_images: b64s }, `${b64s.length} more photo${b64s.length === 1 ? "" : "s"} added`);
      else await update({ [`${kind}_image`]: b64s[0] }, kind === "face" ? "Face photo saved" : "Full-body photo saved");
      setPreview("idle");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save photo"); }
    finally { setBusy(null); }
  };
  const clear = async (kind, idx) => {
    setBusy(kind);
    try { await update(kind === "ref" ? { remove_reference_index: idx } : { [`clear_${kind}`]: true }); setPreview("idle"); } finally { setBusy(null); }
  };
  const render = async (force = false) => {
    setPreview("rendering");
    try { await api.twinPreview(force); setBust(Date.now()); setPreview("ready"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not render your twin"); setPreview("error"); }
  };
  const approve = async (ok) => {
    if (ok) { await update({ twin_approved: true }, "Locked in. Every look will be rendered as you."); }
    else { toast.info("Add 2–3 more photos from different angles, then render again"); }
  };

  if (!profile) return <Skeleton className="h-72 rounded-[var(--radius)]" />;
  const hasAny = profile.has_face || profile.has_body || profile.reference_count > 0;
  const refs = Array.from({ length: profile.reference_count || 0 }, (_, i) => i);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="twin-setup">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {[["face", "Face, straight on", "aspect-square"], ["body", "Full body, arms down", "aspect-[3/4]"]].map(([kind, label, ar]) => {
            const has = profile[`has_${kind}`];
            return (
              <div key={kind}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <label className={`mt-1 relative block ${ar} rounded-2xl border-2 border-dashed overflow-hidden cursor-pointer transition-colors ${has ? "border-transparent" : "border-border hover:border-action"}`} data-testid={`settings-twin-${kind}-drop`}>
                  <input type="file" accept="image/*,.heic" className="hidden" onChange={upload(kind)} data-testid={`settings-twin-${kind}-input`} />
                  {has ? <img src={bust_(api.profileImageUrl(kind), bust)} alt={label} className="h-full w-full object-cover" />
                    : <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground bg-secondary/40"><Camera className="h-6 w-6" /> Tap to add</div>}
                  {busy === kind && <div className="absolute inset-0 bg-background/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>}
                </label>
                {has && <div className="flex gap-3 mt-1.5"><label className="text-xs underline cursor-pointer text-muted-foreground hover:text-foreground">Replace<input type="file" accept="image/*,.heic" className="hidden" onChange={upload(kind)} /></label><button onClick={() => clear(kind)} className="text-xs underline text-muted-foreground hover:text-foreground" data-testid={`settings-twin-${kind}-remove`}>Remove</button></div>}
              </div>
            );
          })}
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">More angles (side profile, smiling, different light) — up to 6. More photos = more <em>you</em>.</Label>
          <div className="mt-1 grid grid-cols-4 sm:grid-cols-6 gap-2">
            {refs.map((i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-secondary" data-testid={`twin-ref-${i}`}>
                <img src={bust_(api.profileImageUrl(`ref${i}`), bust)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => clear("ref", i)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-background/85 flex items-center justify-center" aria-label="Remove photo"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {refs.length < 6 && (
              <label className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-action flex items-center justify-center cursor-pointer transition-colors" data-testid="twin-refs-add">
                <input type="file" accept="image/*,.heic" multiple className="hidden" onChange={upload("refs")} data-testid="twin-refs-input" />
                {busy === "refs" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-5 w-5 text-muted-foreground" />}
              </label>
            )}
          </div>
        </div>
        {!compact && (
          <div>
            <Label className="text-xs text-muted-foreground">Anything the AI must keep exact? (optional)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. curly dirty-blonde hair, beard, dark rectangular glasses" data-testid="settings-twin-notes-input" />
              <Button onClick={() => update({ notes }, "Saved")} variant="secondary" className="rounded-full" data-testid="settings-twin-notes-save"><Check className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Preview + approve loop */}
      <div className="rounded-[var(--radius)] panel border p-4 flex flex-col" data-testid="twin-preview-panel">
        <div className="flex items-center justify-between mb-3">
          <p className="font-display text-xl">Meet your twin</p>
          {profile.twin_approved ? <span className="inline-flex items-center gap-1 rounded-full bg-good text-good-foreground px-2.5 py-1 text-[11px] font-medium" data-testid="twin-approved-badge"><Check className="h-3 w-3" /> Looks like you</span>
            : hasAny ? <span className="inline-flex items-center gap-1 rounded-full bg-wait text-wait-foreground px-2.5 py-1 text-[11px] font-medium" data-testid="twin-pending-badge">Needs your OK</span> : null}
        </div>
        <div className="relative aspect-[3/4] max-h-[460px] rounded-2xl overflow-hidden bg-background border border-border">
          {preview === "ready" ? <img src={bust_(api.twinPreviewUrl(), bust)} alt="Your twin" className="h-full w-full object-cover" data-testid="twin-preview-image" />
            : preview === "rendering" ? <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin text-action" /> Building your twin… about 20 seconds</div>
            : <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6"><Sparkles className="h-7 w-7 text-action" /><p className="text-sm text-muted-foreground">{hasAny ? "Render a test photo of you in a white tee and jeans to check the likeness." : "Add at least one photo of yourself to get started."}</p>{hasAny && <Button onClick={() => render(false)} className="rounded-full" data-testid="twin-render-button">Render my twin</Button>}</div>}
        </div>
        {preview === "ready" && (
          <div className="mt-3 space-y-2">
            <p className="text-sm font-medium">Does this look like you?</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => approve(true)} className="rounded-full bg-good text-good-foreground hover:bg-good/90" data-testid="twin-approve-yes"><ThumbsUp className="h-4 w-4 mr-1.5" /> Yes, that's me</Button>
              <Button variant="outline" onClick={() => approve(false)} className="rounded-full" data-testid="twin-approve-no"><ThumbsDown className="h-4 w-4 mr-1.5" /> Not quite — add more photos</Button>
              <Button variant="ghost" onClick={() => render(true)} className="rounded-full" data-testid="twin-rerender"><RefreshCw className="h-4 w-4 mr-1.5" /> Try again</Button>
            </div>
            <p className="text-xs text-muted-foreground">Tip: a sharp, well-lit face photo plus a side profile makes the biggest difference. Glasses on if you wear them.</p>
          </div>
        )}
      </div>
    </div>
  );
};
